import React, { useEffect, useState } from 'react';
import RadioGroup from '../forms/inputs/radio-group';
import { Provider } from '../forms/utilities/form-context';
import FormItem from '../forms/utilities/form-item';
import ValidationError from '../forms/utilities/validation-error';
import { fire, jsonParse } from '../Libraries/helpers';
import NumberInput from '../forms/inputs/number-input';
import groupBy from 'lodash/groupBy';
import BubbleLoader from '../Libraries/Loader/bubble-loader';

const ENERGY_FOR_1M3 = 0.357;

const HeaterRecommendations = props => {
    const i18n = jsonParse(props.i18n)
    const [loading, setLoading] = useState(true)
    const [products, setProducts] = useState([])
    const [matchingProducts, setMatchingProducts] = useState({})
    const [roomLength, setRoomLength] = useState(5);
    const [roomHeight, setRoomHeight] = useState(10);
    const [roomWidth, setRoomWidth] = useState(5);
    const [wallType, setWallType] = useState(0.5);
    const [indoorTemp, setIndoorTemp] = useState(5);
    const [outdoorTemp, setOutdoorTemp] = useState(-10);
    const [desiredTemp, setDesiredTemp] = useState(20);
    const [results, setResults] = useState({
        roomVolume: 0,
        wattsForRoom: 0,
        wattsLost: 0,
        wattsNeeded: 0,
    });

    const isValid = () => {
        return (
            !isNaN(roomLength) &&
            !isNaN(roomWidth) &&
            !isNaN(roomHeight) &&
            !isNaN(indoorTemp) &&
            !isNaN(outdoorTemp) &&
            !isNaN(desiredTemp) &&
            !isNaN(wallType)
        );
    };

    const calculateResults = () => {
        if (!isValid()) {
            return;
        }

        const areaOfRoom = roomLength * roomWidth;
        const volumeOfRoom = areaOfRoom * roomHeight;
        const totalDispersingArea =
            (roomLength + roomWidth) * (2 * roomHeight) +
            roomWidth * roomLength * 2;
        const temperatureDifferenceDuringTreatment = desiredTemp - indoorTemp;
        const temperatureDifferenceIndoorsOutdoors = desiredTemp - outdoorTemp;
        const wattsRequiredPer1Degree = volumeOfRoom * ENERGY_FOR_1M3;

        const wattsRequiredToHeatRoom =
            (temperatureDifferenceDuringTreatment * wattsRequiredPer1Degree) /
            1000;
        const wattsLostThroughWalls =
            (totalDispersingArea *
                wallType *
                temperatureDifferenceIndoorsOutdoors) /
            1000;
        const totalWattsRequiredToHeatRoom =
            wattsRequiredToHeatRoom + wattsLostThroughWalls;

        setResults({
            roomVolume: volumeOfRoom.toFixed(1),
            wattsForRoom: wattsRequiredToHeatRoom.toFixed(1),
            wattsLost: wattsLostThroughWalls.toFixed(1),
            wattsNeeded: totalWattsRequiredToHeatRoom.toFixed(1),
        });
    };

    useEffect(() => {
        calculateResults();
    }, [
        roomLength,
        roomHeight,
        roomWidth,
        wallType,
        indoorTemp,
        outdoorTemp,
        desiredTemp,
    ]);

    useEffect(() => {
        findMatchingProducts()

        if (products.length) {
            setLoading(false)
        }

    }, [products, results])

    const fetchProducts = async () => {
        const { data } = await axios.get(props.dataSource)

        setProducts(data)
    }

    const findMatchingProducts = () => {
        const boundary = parseFloat(results.wattsNeeded);

        const matches = products.filter(product => {
            return boundary > product.min && boundary < product.max;
        })

        setMatchingProducts(groupBy(matches, 'category'))
    }

    useEffect(() => {
        fire('form-labels::init')

        setTimeout(() => {
            fetchProducts();
        }, 1000)
    }, [])

    return (<Provider i18n={ i18n }>
        <div>

            <h2>{ i18n.room_dimensions }</h2>

            <div className="mt-4 flex flex-wrap">
                <div className="w-full md:w-1/3">
                    <FormItem
                        help={ i18n.in_meters }
                    >
                        <NumberInput
                            text={ i18n.room_length }
                            min={ 0 }
                            required
                            defaultValue={ roomLength }
                            onChange={(value) => setRoomLength(value)}
                        />
                    </FormItem>
                </div>
                <div className="w-full md:w-1/3">
                    <FormItem
                        help={ i18n.in_meters }
                    >
                        <NumberInput
                            text={ i18n.room_width }
                            min={ 0 }
                            required
                            defaultValue={roomWidth}
                            onChange={(value) => setRoomWidth(value)}
                        />
                    </FormItem>
                </div>
                <div className="w-full md:w-1/3">
                    <FormItem
                        help={ i18n.in_meters }
                    >
                        <NumberInput
                            text={ i18n.room_height }
                            min={ 0 }
                            required
                            defaultValue={roomHeight}
                            onChange={(value) => setRoomHeight(value)}
                        />
                    </FormItem>
                </div>
            </div>

            <h2 className="mt-8">{ i18n.room_insulation }</h2>

            <div className="flex flex-wrap">
                <div className="w-full">
                    <FormItem>
                        <RadioGroup
                            text={ i18n.estimated_insulation_type }
                            defaultValue={ wallType }
                            onChange={value => setWallType(value)}
                            required
                            options={[
                                { label: i18n.very_well_insulated, value: 0.5 },
                                { label: i18n.good_insulation, value: 1 },
                                { label: i18n.medium_insulation, value: 2 },
                                { label: i18n.poor_insulation, value: 3 },
                                { label: i18n.no_insulation, value: 4 },
                                { label: i18n.tent, value: 6 },
                            ]}
                        />
                    </FormItem>
                </div>
            </div>

            <h2 className="mt-8">{ i18n.room_temperatures }</h2>

            <div className="mt-4 flex flex-wrap">
                <div className="w-full md:w-1/3">
                    <FormItem
                        help={i18n.in_degrees_celsius}
                    >
                        <NumberInput
                            text={ i18n.current_indoor_temperature }
                            required
                            defaultValue={indoorTemp}
                            onChange={(value) => setIndoorTemp(value)}
                        />
                    </FormItem>
                </div>
                <div className="w-full md:w-1/3">
                    <FormItem
                        help={i18n.in_degrees_celsius}
                    >
                        <NumberInput
                            text={ i18n.current_external_temperature }
                            required
                            defaultValue={outdoorTemp}
                            onChange={(value) => setOutdoorTemp(value)}
                        />
                    </FormItem>
                </div>
                <div className="w-full md:w-1/3">
                    <FormItem
                        help={i18n.in_degrees_celsius}
                    >
                        <NumberInput
                            text={ i18n.desired_indoor_temperature }
                            required
                            defaultValue={desiredTemp}
                            onChange={(value) => setDesiredTemp(value)}
                        />
                    </FormItem>
                </div>
            </div>

            <hr className="mt-6 md:mt-8 border-grey-200" />

            <h2 className="mt-8">{ i18n.results }</h2>

            {!isValid() && (
                <div>
                    <ValidationError>
                        { i18n.errors}
                    </ValidationError>
                </div>
            )}

            {isValid() && (<>
                <div className="mt-4 flex flex-wrap">
                    <div className="w-full sm:w-1/2 md:w-1/3">
                        <FormItem
                            help={<>m<sup>3</sup></>}
                        >
                            <div className={'js-form-label small-label'}>
                                <div className="relative">
                                    <label>{ i18n.volume_of_room }</label>
                                    <input className={'styled-input'} type="number" readOnly value={ results.roomVolume }/>
                                </div>
                            </div>
                        </FormItem>
                    </div>
                    <div className="w-full sm:w-1/2 md:w-1/3">
                        <FormItem
                            help={i18n.kw}
                        >
                            <div className={'js-form-label small-label'}>
                                <div className="relative">
                                    <label>{ i18n.total_energy }</label>
                                    <input style={{ borderColor: '#bccf00', backgroundColor: '#fefff5' }} className={'styled-input'} type="number" readOnly value={ results.wattsNeeded }/>
                                </div>
                            </div>
                        </FormItem>
                    </div>
                </div>

                { loading && <>
                    <h2 className="mt-4 md:mt-8 font-semibold underline underline-offset-2">{i18n.recommended_products}</h2>
                    <BubbleLoader />
                </> }

                {Object.keys(matchingProducts).length ? <>
                    <h2 className="mt-4 md:mt-8 font-semibold underline underline-offset-2">{i18n.recommended_products}</h2>
                    <div className={'md:mt-4 flex flex-wrap'}>
                        {Object.keys(matchingProducts).map(category => <div key={category}
                            className={'mt-6 w-full sm:w-1/2 md:w-1/3'}>
                            <div className={'pr-10'}>
                                <h3 className={'text-sm font-bold'}>{category}</h3>

                                <div>
                                    {matchingProducts[category].map(product => <div key={product.key}>
                                        <div className={'mt-4 flex'}>
                                            <div className={'w-20'}>
                                                <a href={product.url}>
                                                    <img src={product.image.url} alt={product.image.alt}
                                                        loading={'lazy'} width={200} height={200}/>
                                                </a>
                                            </div>
                                            <div className={'flex-1 pl-4'}>
                                                <h4 className={'leading-snug text-[0.9375rem]'}>
                                                    <a className={'inline-flex underline decoration-transparent transition ease-linear duration-300 hover:decoration-current'}
                                                        href={product.url}>
                                                        {product.title}
                                                    </a>
                                                </h4>
                                                <p className={'text-sm text-grey-500 mt-0.5'}>{product.strap}</p>
                                                <a className={'text-sm text-grey-500 mt-0.5 underline decoration-current transition ease-linear duration-300 hover:text-green'}
                                                    href={product.url}>
                                                    {i18n.view_product} <span
                                                        className={'sr-only'}>{product.title}</span>
                                                </a>
                                            </div>
                                        </div>
                                    </div>)}
                                </div>

                            </div>
                        </div>)}
                    </div>
                </> : null}
            </>)}
        </div>
    </Provider>
    );
}

export default HeaterRecommendations;
